import type { StripePriceAttributeProps } from "@circle-react/components/Paywalls/Admin/PaywallsAdmin/FormModal/types";
import type { useCouponPreviewMutation } from "@circle-react/components/Paywalls/Checkout/hooks/useCouponPreviewMutation";
import {
  CouponApplicationStatus,
  useGetCouponApplication,
} from "@circle-react/components/Paywalls/Checkout/hooks/useGetCouponApplication";
import { type ReactElement, cloneElement } from "react";

import { usePaywallCustomizationContext } from "../PaywallCustomizationProvider";
import { Offer } from "./Offer";
import { useGetReducedPrice } from "./useGetReducedPrice";

interface InjectedChildProps {
  couponApplications?: ReturnType<typeof useGetCouponApplication>;
  reducedPrice?: ReturnType<typeof useGetReducedPrice>;
}

interface ReducedPriceProps {
  children: ReactElement<InjectedChildProps>;
  couponPreviewMutation: ReturnType<typeof useCouponPreviewMutation>;
  price: StripePriceAttributeProps;
}

export const ReducedPrice = Object.assign(
  ({ children, price, couponPreviewMutation }: ReducedPriceProps) => {
    const { paywallCustomization } = usePaywallCustomizationContext();
    const couponApplications = useGetCouponApplication(
      Number(price.id),
      couponPreviewMutation,
    );

    const hasCoupons =
      couponApplications.status !== CouponApplicationStatus.NO_COUPON_APPLIED;

    const reducedPrice = useGetReducedPrice({
      priceId: Number(price.id),
      tempId: price.tmp_id,
      reducedPriceProperties: paywallCustomization?.reduced_price_properties,
    });

    if (!reducedPrice || !reducedPrice.enabled) {
      return cloneElement(children, {
        couponApplications,
      });
    }

    return cloneElement(children, {
      couponApplications,
      reducedPrice: hasCoupons ? undefined : reducedPrice,
    });
  },
  {
    Offer,
  },
);
